import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import RecommendedResources from '../components/RecommendedResources';

function ResultsPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [results, setResults] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchResults();
  }, [id, user]);
  
  const fetchResults = async () => {
    try {
      const response = await axios.get(`/api/assessments/${id}/results`);
      setResults(response.data.results);
      setAssessment(response.data.assessment);
    } catch (error) {
      toast.error('Failed to load results');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      // First generate the PDF
      const generateResponse = await axios.post(`/api/assessments/${id}/generate-pdf`);
      
      if (generateResponse.data.success) {
        // Then download it
        const pdfId = generateResponse.data.pdfId;
        const downloadUrl = `/api/pdf/${pdfId}/download`;
        
        // Determine the assessment type for the filename
        const assessmentType = type || assessment?.type;
        
        // Create a link and trigger download
        const link = document.createElement('a');
        link.href = `${axios.defaults.baseURL}${downloadUrl}`;
        link.setAttribute('download', `SecretSauce_${assessmentType === 'fear' ? 'EDGE' : 'CORE'}_Report.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('PDF downloaded successfully!');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate PDF';
      
      // Check if it's a service unavailable error - offer browser print option
      if (error.response?.status === 503) {
        toast.error(
          <div>
            <p className="font-medium">PDF service temporarily unavailable</p>
            <p className="text-sm mt-1">Use Ctrl+P (or Cmd+P on Mac) to print this page to PDF</p>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setPdfLoading(false);
    }
  };
  
  const handleEmailPDF = async () => {
    setEmailLoading(true);
    try {
      const response = await axios.post(`/api/assessments/${id}/email-pdf`);
      
      if (response.data.success) {
        toast.success('PDF report sent to your email!');
      }
    } catch (error) {
      console.error('Email error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send email';
      
      if (error.response?.status === 503) {
        toast.error(
          <div>
            <p className="font-medium">Email service temporarily unavailable</p>
            <p className="text-sm mt-1">Use the Download button or print this page instead</p>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setEmailLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="container-custom py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="container-custom py-12 max-w-4xl">
          <div className="space-y-8">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </main>
      </div>
    );
  }
  
  // Determine the assessment type - use URL param if available, otherwise use assessment data
  const assessmentType = type || assessment?.type;
  
  if (assessmentType === 'fear') {
    return (
      <FearResults 
        results={results} 
        assessment={assessment}
        onDownloadPDF={handleDownloadPDF}
        onEmailPDF={handleEmailPDF}
        pdfLoading={pdfLoading}
        emailLoading={emailLoading}
      />
    );
  } else {
    return (
      <ArchetypeResults 
        results={results} 
        assessment={assessment}
        onDownloadPDF={handleDownloadPDF}
        onEmailPDF={handleEmailPDF}
        pdfLoading={pdfLoading}
        emailLoading={emailLoading}
      />
    );
  }
}

function FearResults({ results, assessment, onDownloadPDF, onEmailPDF, pdfLoading, emailLoading }) {
  const navigate = useNavigate();
  
  // Helper to format scores - handles both decimal (0.38) and percentage (38) formats
  const formatScore = (score) => {
    if (score === null || score === undefined) return '0%';
    // If score is less than 1, it's a decimal probability
    if (score < 1) {
      return `${Math.round(score * 100)}%`;
    }
    // Otherwise it's already a percentage
    return `${Math.round(score)}%`;
  };
  
  const intensityConfig = {
    'background': { 
      bg: 'bg-blue-50', 
      text: 'text-blue-700', 
      border: 'border-blue-200',
      label: 'Background'
    },
    'active': { 
      bg: 'bg-amber-50', 
      text: 'text-amber-700', 
      border: 'border-amber-200',
      label: 'Active'
    },
    'acute': { 
      bg: 'bg-red-50', 
      text: 'text-red-700', 
      border: 'border-red-200',
      label: 'Acute'
    },
    'active-low': { 
      bg: 'bg-amber-50', 
      text: 'text-amber-700', 
      border: 'border-amber-200',
      label: 'Active (Low)'
    }
  };
  
  const primaryIntensity = intensityConfig[results?.primary_fear_intensity] || intensityConfig['active'];
  const secondaryIntensity = intensityConfig[results?.secondary_fear_intensity] || intensityConfig['background'];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  // Detect v9 format
  const isV9 = results?.format_version === 'v9' || results?.profile_markdown;

  // Helper to render markdown text (strips ** and * formatting, renders as React)
  const renderMd = (text) => {
    if (!text) return null;
    // Convert **bold** to <strong> and *italic* to <em>
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  // Helper to render paragraphs with markdown
  const renderMdParagraphs = (text) => {
    if (!text) return null;
    return text.split('\n\n').map((p, i) => <p key={i}>{renderMd(p.trim())}</p>);
  };

  if (isV9) {
    return (
      <div className="max-w-4xl" data-testid="fear-results">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">EDGE Diagnostic Results</h1>
          <p className="text-sm text-muted-foreground mt-1">Your personalised Edge Profile</p>
        </div>

        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="space-y-8">
          
          {/* The Pattern */}
          {results.the_pattern && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <span className="eyebrow text-primary mb-3 block">The Pattern</span>
              <div className="body text-muted-foreground space-y-4 leading-relaxed">
                {renderMdParagraphs(results.the_pattern)}
              </div>
            </motion.div>
          )}

          {/* Primary Edge */}
          <motion.div variants={fadeInUp} className="bg-gradient-to-br from-primary/5 to-violet-50 dark:from-primary/10 dark:to-violet-950/20 border border-primary/20 rounded-[var(--radius)] p-8 elev-1">
            <span className="eyebrow text-primary mb-2 block">Primary Edge</span>
            <h2 className="h2 text-3xl mb-6" data-testid="primary-fear-cluster">{results.primary_fear_cluster || results.primary_edge_title || 'Not identified'}</h2>
            {results.primary_pressure && (
              <div className="mb-4">
                <p className="font-semibold text-sm text-muted-foreground mb-1">WHERE THE PRESSURE LANDS</p>
                <p className="body">{renderMd(results.primary_pressure)}</p>
              </div>
            )}
            {results.primary_touches && (
              <div className="mb-4">
                <p className="font-semibold text-sm text-muted-foreground mb-1">WHAT IT TOUCHES</p>
                <p className="body">{renderMd(results.primary_touches)}</p>
              </div>
            )}
            {results.primary_question && (
              <div className="p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                <p className="font-semibold text-sm text-muted-foreground mb-1">THE QUESTION UNDERNEATH</p>
                <p className="body italic">{renderMd(results.primary_question)}</p>
              </div>
            )}
          </motion.div>

          {/* Secondary Edge */}
          {(results.secondary_fear_cluster || results.secondary_edge_title) && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <span className="eyebrow text-violet-600 mb-2 block">Secondary Edge</span>
              <h3 className="font-serif text-2xl font-semibold mb-4">{results.secondary_fear_cluster || results.secondary_edge_title}</h3>
              {results.secondary_edge_content && (
                <div className="body text-muted-foreground space-y-4 leading-relaxed">
                  {results.secondary_edge_content.split('\n\n').map((p, i) => (
                    <p key={i}>{renderMd(p.replace(/\*\*The interaction:\*\*\s*/, ''))}</p>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Surface & Root */}
          {(results.surface_concern || results.root_concern) && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-6">Surface & Root</h3>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-muted-foreground/30">
                  <p className="font-semibold text-sm text-muted-foreground mb-2">WHAT YOU'D SAY IF ASKED</p>
                  <p className="body">{renderMd(results.surface_concern)}</p>
                </div>
                <div className="p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                  <p className="font-semibold text-sm text-muted-foreground mb-2">WHAT'S ACTUALLY DRIVING THINGS</p>
                  <p className="body">{renderMd(results.root_concern)}</p>
                </div>
              </div>
              {results.the_gap && (
                <div>
                  <p className="font-semibold text-sm text-muted-foreground mb-2">THE GAP</p>
                  <p className="body text-muted-foreground leading-relaxed">{renderMd(results.the_gap)}</p>
                </div>
              )}
            </motion.div>
          )}

          {/* The Hidden Logic */}
          {results.the_hidden_logic && (
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/30 dark:to-gray-950/30 border border-slate-200 dark:border-slate-800 rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-4">The Hidden Logic</h3>
              <div className="body text-muted-foreground space-y-4 leading-relaxed">
                {renderMdParagraphs(results.the_hidden_logic)}
              </div>
            </motion.div>
          )}

          {/* The Reframe */}
          {results.the_reframe && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-4">The Reframe</h3>
              <div className="body text-muted-foreground space-y-4 leading-relaxed">
                {renderMdParagraphs(results.the_reframe)}
              </div>
            </motion.div>
          )}

          {/* What EDGE Doesn't Answer */}
          {results.what_edge_doesnt_answer && (
            <motion.div variants={fadeInUp} className="bg-primary/5 border border-primary/20 rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-4">What EDGE Doesn't Answer</h3>
              <p className="body text-muted-foreground leading-relaxed">{renderMd(results.what_edge_doesnt_answer)}</p>
            </motion.div>
          )}
        </motion.div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button onClick={onDownloadPDF} disabled={pdfLoading} data-testid="download-pdf-btn">
            {pdfLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : 'Download PDF Report'}
          </Button>
          <Button variant="outline" onClick={onEmailPDF} disabled={emailLoading}>
            {emailLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Email Report'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="fear-results">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">EDGE Diagnostic Results</h1>
        <p className="text-sm text-muted-foreground mt-1">Your personalised Edge Profile</p>
      </div>
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
            visible: { transition: { staggerChildren: 0.1 } }
          }}
          className="space-y-8"
        >
          {/* Edge Profile Summary */}
          <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
            <div className="mb-6">
              <span className="eyebrow text-primary mb-2 block">Edge Profile Summary</span>
              <p className="body text-muted-foreground">
                This diagnostic has identified patterns in how pressure shows up in your leadership. 
                Understanding these patterns is the first step toward leveraging them for growth.
              </p>
            </div>
          </motion.div>

          {/* Primary Edge */}
          <motion.div variants={fadeInUp} className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800 rounded-[var(--radius)] p-8 elev-1">
            <div className="mb-6">
              <span className="eyebrow text-amber-600 dark:text-amber-400 mb-2 block">Primary Pressure Point</span>
              <h2 className="h2 text-3xl mb-4" data-testid="primary-fear-cluster">{results?.primary_fear_cluster || 'Not identified'}</h2>
              <div className="flex flex-wrap items-center gap-4">
                <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${primaryIntensity.bg} ${primaryIntensity.text} ${primaryIntensity.border} border`}>
                  {primaryIntensity.label} Intensity
                </span>
              </div>
            </div>
          </motion.div>
          
          {/* Surface vs Root Analysis - supports both legacy and v8 format */}
          {(results?.root_fear || results?.depth_analysis) && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-6 text-foreground">Surface vs Root Analysis</h3>
              
              <div className="space-y-6">
                <div className="p-4 bg-muted/30 rounded-[var(--radius)] border-l-4 border-muted-foreground/30">
                  <p className="font-semibold text-sm text-muted-foreground mb-2">SURFACE PRESSURE</p>
                  <p className="body">{results?.surface_pressure || results?.surface_fear || results?.depth_analysis?.surface_pressure?.stated_concern}</p>
                </div>
                
                <div className="p-4 bg-primary/5 rounded-[var(--radius)] border-l-4 border-primary">
                  <p className="font-semibold text-sm text-primary mb-2">ROOT PRESSURE</p>
                  <p className="body font-medium">{results?.root_pressure || results?.root_fear || results?.depth_analysis?.root_pressure?.hidden_concern}</p>
                </div>
                
                {(results?.surface_root_connection || results?.depth_analysis?.surface_root_gap?.gap_description) && (
                  <div className="p-4 bg-secondary/10 rounded-[var(--radius)]">
                    <p className="body text-sm italic text-muted-foreground">{results?.surface_root_connection || results?.depth_analysis?.surface_root_gap?.gap_description}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* V8 Deeper Patterns - Only shown for v8 format */}
          {results?.deeper_patterns && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-6 text-foreground">Deeper Patterns</h3>
              
              <div className="space-y-6">
                {results.deeper_patterns.origin_hypothesis?.likely_formation && (
                  <div className="p-4 bg-violet-50 dark:bg-violet-950/30 rounded-[var(--radius)]">
                    <p className="font-semibold text-sm text-violet-600 dark:text-violet-400 mb-2">ORIGIN</p>
                    <p className="body">{results.deeper_patterns.origin_hypothesis.likely_formation}</p>
                  </div>
                )}
                
                {results.deeper_patterns.shadow_dynamics?.what_theyre_avoiding && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-950/30 rounded-[var(--radius)]">
                    <p className="font-semibold text-sm text-slate-600 dark:text-slate-400 mb-2">WHAT YOU MAY BE AVOIDING</p>
                    <p className="body">{results.deeper_patterns.shadow_dynamics.what_theyre_avoiding}</p>
                  </div>
                )}
                
                {results.deeper_patterns.hidden_costs && (
                  <div className="p-4 bg-rose-50 dark:bg-rose-950/30 rounded-[var(--radius)]">
                    <p className="font-semibold text-sm text-rose-600 dark:text-rose-400 mb-2">HIDDEN COSTS</p>
                    <div className="grid gap-2 mt-2">
                      {results.deeper_patterns.hidden_costs.energy_drain && (
                        <p className="body text-sm"><span className="font-medium">Energy:</span> {results.deeper_patterns.hidden_costs.energy_drain}</p>
                      )}
                      {results.deeper_patterns.hidden_costs.opportunity_cost && (
                        <p className="body text-sm"><span className="font-medium">Opportunity:</span> {results.deeper_patterns.hidden_costs.opportunity_cost}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* V8 The Paradox */}
          {results?.the_paradox?.the_trap && (
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border border-purple-200 dark:border-purple-800 rounded-[var(--radius)] p-8">
              <h3 className="font-serif text-xl font-semibold mb-4 text-purple-800 dark:text-purple-200">The Paradox</h3>
              <p className="body text-purple-700 dark:text-purple-300 leading-relaxed mb-4">
                {results.the_paradox.the_trap}
              </p>
              {results.the_paradox.the_irony && (
                <p className="body text-sm italic text-purple-600 dark:text-purple-400">
                  {results.the_paradox.the_irony}
                </p>
              )}
            </motion.div>
          )}
          
          {/* V8 Personalised Insights */}
          {results?.personalised_insights?.what_this_means_for_you && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
              <h3 className="font-serif text-xl font-semibold mb-4 text-foreground">What This Means For You</h3>
              <p className="body text-muted-foreground leading-relaxed">
                {results.personalised_insights.what_this_means_for_you}
              </p>
            </motion.div>
          )}
          {/* Secondary Concern */}
          {results?.secondary_fear_cluster && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <span className="eyebrow text-muted-foreground mb-2 block">Secondary Concern</span>
              <h3 className="h3 text-xl mb-3" data-testid="secondary-fear-cluster">{results?.secondary_fear_cluster}</h3>
              <div className="flex flex-wrap items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${secondaryIntensity.bg} ${secondaryIntensity.text} ${secondaryIntensity.border} border`}>
                  {secondaryIntensity.label} Intensity
                </span>
              </div>
            </motion.div>
          )}
          
          {/* The Hidden Opportunity */}
          <motion.div variants={fadeInUp} className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-[var(--radius)] p-8">
            <h3 className="font-serif text-xl font-semibold mb-4 text-emerald-800">The Hidden Opportunity</h3>
            <p className="body text-emerald-700 leading-relaxed">
              {results?.the_reframe?.alternative_frame || results?.reframe || `Your ${results?.primary_fear_cluster || 'fear pattern'} isn't a weakness to overcome—it's a signal pointing toward your growth edge. The intensity of this fear often correlates with the magnitude of the opportunity on the other side.`}
            </p>
            {results?.the_reframe?.what_if_question && (
              <p className="body text-emerald-600 mt-4 font-medium italic">
                {results.the_reframe.what_if_question}
              </p>
            )}
          </motion.div>
          
          {/* Key Observations - supports both formats */}
          {(results?.key_observations?.length > 0 || results?.response_observations?.notable_moments?.length > 0) && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <h3 className="font-semibold mb-4">Observations from Assessment</h3>
              <ul className="space-y-3">
                {(results?.key_observations || results?.response_observations?.notable_moments || []).map((obs, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1 text-lg">•</span>
                    <span className="body">{typeof obs === 'string' ? obs : obs.observation || obs}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
          
          {/* Notable Moments */}
          {results?.notable_moments && results.notable_moments.length > 0 && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <h3 className="font-semibold mb-4">Notable Moments</h3>
              <ul className="space-y-3">
                {results.notable_moments.map((moment, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-secondary-foreground mt-1">→</span>
                    <span className="body text-sm text-muted-foreground italic">{moment}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
          
          {/* Download & Email Section */}
          <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
            <h3 className="font-semibold mb-4">Download Your Report</h3>
            <p className="body text-muted-foreground mb-6">
              Get a beautifully formatted PDF of your EDGE Diagnostic results to keep or share.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={onDownloadPDF}
                disabled={pdfLoading}
                className="bg-primary hover:bg-[hsl(200_64%_34%)]"
                data-testid="download-pdf-btn"
              >
                {pdfLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF
                  </>
                )}
              </Button>
              
              <Button 
                onClick={onEmailPDF}
                disabled={emailLoading}
                variant="outline"
                data-testid="email-pdf-btn"
              >
                {emailLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Report
                  </>
                )}
              </Button>
            </div>
          </motion.div>
          
          {/* Recommended Resources */}
          <motion.div variants={fadeInUp}>
            <RecommendedResources 
              fearCluster={results?.primary_fear?.cluster || results?.fear_cluster}
              maxItems={4}
            />
          </motion.div>
          
          {/* What's Next CTA */}
          <motion.div variants={fadeInUp} className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-[var(--radius)] p-8">
            <h3 className="font-serif text-xl font-semibold mb-4">What's Next?</h3>
            <p className="body mb-6 text-muted-foreground">
              Now that you've identified your pressure patterns, the CORE Diagnostic will reveal how you operate as a leader — and where the real growth opportunity lies.
            </p>
            <Button
              onClick={() => navigate('/assessment/archetype/start')}
              className="bg-primary hover:bg-[hsl(200_64%_34%)]"
              data-testid="continue-to-archetype"
            >
              Continue to CORE Diagnostic
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </motion.div>
        </motion.div>
    </div>
  );
}

function ArchetypeResults({ results, assessment, onDownloadPDF, onEmailPDF, pdfLoading, emailLoading }) {
  const navigate = useNavigate();
  const [fearResults, setFearResults] = useState(null);
  
  useEffect(() => {
    // Try to fetch EDGE diagnostic results for context
    const fetchFearResults = async () => {
      try {
        const response = await axios.get('/api/assessments');
        const fearAssessment = response.data.assessments?.find(
          a => a.type === 'fear' && a.status === 'completed'
        );
        if (fearAssessment) {
          const fearResponse = await axios.get(`/api/assessments/${fearAssessment.id}/results`);
          setFearResults(fearResponse.data.results);
        }
      } catch (error) {
        console.log('Could not fetch fear results for context');
      }
    };
    fetchFearResults();
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const hasGap = results?.conscious_unconscious_gap;
  
  return (
    <div className="max-w-4xl" data-testid="archetype-results">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">CORE Diagnostic Results</h1>
        <p className="text-sm text-muted-foreground mt-1">Your leadership operating profile</p>
      </div>
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.1 } }
        }}
        className="space-y-8"
      >
        {/* Primary & Secondary Archetypes */}
        <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border border-violet-200 dark:border-violet-800 rounded-[var(--radius)] p-6 elev-1">
            <span className="eyebrow text-violet-600 dark:text-violet-400 mb-2 block">Stated Archetype</span>
            <h2 className="h2 text-2xl mb-2" data-testid="primary-archetype">{results?.primary_archetype || 'Not identified'}</h2>
            </div>
            
            <div className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <span className="eyebrow text-muted-foreground mb-2 block">Secondary Archetype</span>
              <h3 className="h3 text-xl mb-2" data-testid="secondary-archetype">{results?.secondary_archetype || 'Not identified'}</h3>
            </div>
          </motion.div>
          
          {/* Stated vs Core Analysis */}
          <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
            <h3 className="font-serif text-xl font-semibold mb-6">Stated vs. Core Analysis</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-4 bg-muted/30 rounded-[var(--radius)]">
                <span className="text-sm text-muted-foreground mb-2 block">How you see yourself:</span>
                <p className="font-semibold text-lg">{results?.conscious_archetype || 'N/A'}</p>
              </div>
              <div className="p-4 bg-primary/5 rounded-[var(--radius)]">
                <span className="text-sm text-muted-foreground mb-2 block">How you actually operate:</span>
                <p className="font-semibold text-lg">{results?.unconscious_archetype || 'N/A'}</p>
              </div>
            </div>
            
            {/* Gap Analysis */}
            {hasGap ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-[var(--radius)]">
                <p className="font-semibold mb-2 text-amber-700 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Gap Detected
                </p>
                <p className="body text-amber-800">{results?.gap_description || 'There is a notable difference between how you perceive yourself and how you actually operate.'}</p>
              </div>
            ) : (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-[var(--radius)]">
                <p className="font-semibold text-emerald-700 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Aligned
                </p>
                <p className="body text-sm text-emerald-700">Your self-perception matches how you operate.</p>
              </div>
            )}
          </motion.div>
          
          {/* Archetype Tension */}
          {results?.archetype_tension && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <h3 className="font-semibold mb-3">Archetype Tension</h3>
              <p className="body text-muted-foreground">{results.archetype_tension}</p>
            </motion.div>
          )}
          
          {/* Growth Edge */}
          {results?.growth_edge && (
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-[var(--radius)] p-8">
              <h3 className="font-serif text-xl font-semibold mb-4 text-emerald-800">Your Growth Edge</h3>
              <p className="body text-emerald-700 font-medium leading-relaxed">{results.growth_edge}</p>
            </motion.div>
          )}
          
          {/* Connection to Edge Profile */}
          {fearResults && (
            <motion.div variants={fadeInUp} className="bg-orange-50 border border-orange-200 rounded-[var(--radius)] p-6">
              <h3 className="font-semibold mb-4 text-orange-800">Connection to Your Edge Profile</h3>
              <p className="body text-orange-700 mb-4">
                Your primary pressure point (<strong>{fearResults.primary_fear_cluster}</strong>) interacts with your {results?.primary_archetype} archetype in significant ways.
              </p>
              <p className="body text-sm text-orange-600">
                {results?.fear_archetype_connection || 'This relationship often manifests in how you approach risk, make decisions under pressure, and navigate uncertainty.'}
              </p>
            </motion.div>
          )}
          
          {/* Key Observations */}
          {results?.key_observations && results.key_observations.length > 0 && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <h3 className="font-semibold mb-4">Key Observations</h3>
              <ul className="space-y-3">
                {results.key_observations.map((obs, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1 text-lg">•</span>
                    <span className="body">{obs}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
          
          {/* Practical Implications */}
          {results?.pillar1_relevance && (
            <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <h3 className="font-semibold mb-3">Practical Implications</h3>
              <p className="body text-muted-foreground">{results.pillar1_relevance}</p>
            </motion.div>
          )}
          
          {/* Download & Email Section */}
          <motion.div variants={fadeInUp} className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
            <h3 className="font-semibold mb-4">Download Your Report</h3>
            <p className="body text-muted-foreground mb-6">
              Get a beautifully formatted PDF of your CORE Diagnostic results to keep or share.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={onDownloadPDF}
                disabled={pdfLoading}
                className="bg-primary hover:bg-[hsl(200_64%_34%)]"
                data-testid="download-pdf-btn"
              >
                {pdfLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF
                  </>
                )}
              </Button>
              
              <Button 
                onClick={onEmailPDF}
                disabled={emailLoading}
                variant="outline"
                data-testid="email-pdf-btn"
              >
                {emailLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Report
                  </>
                )}
              </Button>
            </div>
          </motion.div>
          
          {/* Recommended Resources */}
          <motion.div variants={fadeInUp}>
            <RecommendedResources 
              archetype={results?.primary_archetype?.name || results?.archetype}
              maxItems={4}
            />
          </motion.div>
          
          {/* What's Next CTA - Apply for Pilot */}
          <motion.div variants={fadeInUp} className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-[var(--radius)] p-8">
            <h3 className="font-serif text-xl font-semibold mb-4">Ready for the Next Step?</h3>
            <p className="body mb-6 text-muted-foreground">
              You've completed both diagnostics. Consider applying for the PATH Programme to work with these insights in a facilitated environment.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate('/apply')}
                className="bg-primary hover:bg-[hsl(200_64%_34%)]"
                data-testid="apply-for-pilot"
              >
                Apply for PATH
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
              >
                Back to Dashboard
              </Button>
            </div>
          </motion.div>
        </motion.div>
    </div>
  );
}

export default ResultsPage;
